import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Trash2, Check, X, Users, Tag } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import api from '../utils/api';

export default function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, pagesRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/roles'),
        api.get('/admin/pages'),
      ]);
      setUsers(usersRes.data.users || []);
      setRoles(rolesRes.data.roles || []);
      setPages(pagesRes.data.pages || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabs = [
    { id: 'users', label: 'Brugere', icon: Users },
    { id: 'roles', label: 'Roller', icon: Tag },
  ];

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="admin-page">
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-4">
          <Navigation />
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#1E6F7A]" />
            Administration
          </h1>
        </div>
      </header>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="flex gap-1 mb-6 bg-white/5 rounded-lg p-1 w-fit">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === t.id ? 'bg-[#1E6F7A] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" /></div>
        ) : activeTab === 'users' ? (
          <UserManagement users={users} roles={roles} pages={pages} onRefresh={fetchAll} />
        ) : (
          <RoleManagement roles={roles} pages={pages} onRefresh={fetchAll} />
        )}
      </div>
    </div>
  );
}

function UserManagement({ users, roles, pages, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [editUser, setEditUser] = useState(null);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    await api.post('/admin/users', { email: newEmail, name: newName, pages: [], roles: [], active: true });
    setNewEmail(''); setNewName(''); setShowAdd(false);
    onRefresh();
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Slet bruger ${email}?`)) return;
    await api.delete('/admin/users', { data: { email } });
    onRefresh();
  };

  const handleToggleActive = async (user) => {
    await api.put('/admin/users', { email: user.email, active: !user.active });
    onRefresh();
  };

  const handleSavePages = async (email, selectedPages, selectedRoles) => {
    await api.put('/admin/users', { email, pages: selectedPages, roles: selectedRoles });
    setEditUser(null);
    onRefresh();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">Brugere ({users.length})</h2>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-user-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E6F7A]/20 hover:bg-[#1E6F7A]/30 text-[#1E6F7A] rounded text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Tilføj bruger
        </button>
      </div>

      {showAdd && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 flex items-end gap-3" data-testid="add-user-form">
          <div className="flex-1">
            <label className="block text-white/50 text-xs mb-1">Email</label>
            <input data-testid="new-user-email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
              placeholder="email@example.com" className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          <div className="w-48">
            <label className="block text-white/50 text-xs mb-1">Navn</label>
            <input data-testid="new-user-name" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="Navn" className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          <button onClick={handleAdd} className="p-2 bg-[#1E6F7A] text-white rounded"><Check className="w-4 h-4" /></button>
          <button onClick={() => setShowAdd(false)} className="p-2 bg-white/5 text-white/60 rounded"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="bg-[#111318] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-white/10 text-white/50 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Navn</th>
              <th className="text-left px-4 py-3">Roller</th>
              <th className="text-center px-4 py-3">Sider</th>
              <th className="text-center px-4 py-3">Aktiv</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, idx) => (
              <React.Fragment key={u.email}>
                <tr className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{u.email}</td>
                  <td className="px-4 py-3 text-white/60">{u.name || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {(u.roles || []).map(r => (
                        <span key={r} className="bg-purple-500/20 text-purple-300 text-xs px-2 py-0.5 rounded">{r}</span>
                      ))}
                      {(!u.roles || u.roles.length === 0) && <span className="text-white/20 text-xs">Ingen</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-white/50 text-xs">{(u.pages || []).length} sider</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleToggleActive(u)}
                      className={`w-8 h-5 rounded-full transition-colors ${u.active ? 'bg-[#1E6F7A]' : 'bg-white/10'}`}>
                      <div className={`w-3.5 h-3.5 rounded-full bg-white transition-transform ${u.active ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 flex gap-1 justify-end">
                    <button onClick={() => setEditUser(editUser === u.email ? null : u.email)} data-testid={`edit-user-${idx}`}
                      className="px-2 py-1 text-xs bg-white/5 hover:bg-white/10 text-white/60 rounded transition-colors">
                      Rediger
                    </button>
                    <button onClick={() => handleDelete(u.email)} data-testid={`delete-user-${idx}`}
                      className="p-1 hover:bg-red-400/10 text-white/20 hover:text-red-400 rounded transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
                {editUser === u.email && (
                  <tr><td colSpan={6} className="px-4 py-4 bg-white/[0.02]">
                    <PageRoleEditor user={u} pages={pages} roles={roles} onSave={handleSavePages} onCancel={() => setEditUser(null)} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PageRoleEditor({ user, pages, roles, onSave, onCancel }) {
  const [selectedPages, setSelectedPages] = useState(user.pages || []);
  const [selectedRoles, setSelectedRoles] = useState(user.roles || []);

  const togglePage = (path) => {
    setSelectedPages(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const toggleRole = (name) => {
    setSelectedRoles(prev => prev.includes(name) ? prev.filter(r => r !== name) : [...prev, name]);
  };
  const selectAll = () => setSelectedPages(pages.map(p => p.path));
  const selectNone = () => setSelectedPages([]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-white/60 text-xs font-semibold uppercase tracking-wider">Adgang for {user.email}</p>
        <div className="flex gap-2">
          <button onClick={selectAll} className="text-xs text-[#1E6F7A] hover:underline">Vælg alle</button>
          <button onClick={selectNone} className="text-xs text-white/40 hover:underline">Fjern alle</button>
        </div>
      </div>
      {roles.length > 0 && (
        <div className="mb-3">
          <p className="text-white/40 text-xs mb-2">Roller:</p>
          <div className="flex gap-2 flex-wrap">
            {roles.map(r => (
              <button key={r.name} onClick={() => toggleRole(r.name)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  selectedRoles.includes(r.name) ? 'bg-purple-500/30 text-purple-300 border border-purple-500/40' : 'bg-white/5 text-white/40 border border-white/10'
                }`}>{r.name} ({(r.pages || []).length} sider)</button>
            ))}
          </div>
        </div>
      )}
      <p className="text-white/40 text-xs mb-2">Direkte sideadgang:</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 mb-4">
        {pages.map(p => (
          <button key={p.path} onClick={() => togglePage(p.path)}
            className={`text-left px-3 py-1.5 rounded text-xs transition-colors ${
              selectedPages.includes(p.path) ? 'bg-[#1E6F7A]/20 text-[#1E6F7A] border border-[#1E6F7A]/30' : 'bg-white/5 text-white/40 border border-white/5'
            }`}>{p.label}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(user.email, selectedPages, selectedRoles)}
          className="px-4 py-1.5 bg-[#1E6F7A] text-white rounded text-sm font-medium">Gem</button>
        <button onClick={onCancel}
          className="px-4 py-1.5 bg-white/5 text-white/60 rounded text-sm">Annuller</button>
      </div>
    </div>
  );
}

function RoleManagement({ roles, pages, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [editRole, setEditRole] = useState(null);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    await api.post('/admin/roles', { name: newName, pages: [] });
    setNewName(''); setShowAdd(false);
    onRefresh();
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Slet rollen "${name}"? Den fjernes fra alle brugere.`)) return;
    await api.delete('/admin/roles', { data: { name } });
    onRefresh();
  };

  const handleSavePages = async (name, selectedPages) => {
    await api.put('/admin/roles', { name, pages: selectedPages });
    setEditRole(null);
    onRefresh();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">Roller ({roles.length})</h2>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-role-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E6F7A]/20 hover:bg-[#1E6F7A]/30 text-[#1E6F7A] rounded text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Opret rolle
        </button>
      </div>

      {showAdd && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-white/50 text-xs mb-1">Rollenavn</label>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="F.eks. Konsulent"
              className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          <button onClick={handleAdd} className="p-2 bg-[#1E6F7A] text-white rounded"><Check className="w-4 h-4" /></button>
          <button onClick={() => setShowAdd(false)} className="p-2 bg-white/5 text-white/60 rounded"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="space-y-3">
        {roles.map(r => (
          <div key={r.name} className="bg-[#111318] border border-white/10 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="text-white font-medium">{r.name}</span>
                <span className="text-white/30 text-xs ml-2">{(r.pages || []).length} sider</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditRole(editRole === r.name ? null : r.name)}
                  className="px-2 py-1 text-xs bg-white/5 hover:bg-white/10 text-white/60 rounded">Rediger</button>
                <button onClick={() => handleDelete(r.name)}
                  className="p-1 hover:bg-red-400/10 text-white/20 hover:text-red-400 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {editRole === r.name && (
              <RolePageEditor role={r} pages={pages} onSave={handleSavePages} onCancel={() => setEditRole(null)} />
            )}
          </div>
        ))}
        {roles.length === 0 && <p className="text-white/30 text-sm text-center py-8">Ingen roller oprettet endnu</p>}
      </div>
    </div>
  );
}

function RolePageEditor({ role, pages, onSave, onCancel }) {
  const [selected, setSelected] = useState(role.pages || []);
  const toggle = (path) => setSelected(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);

  return (
    <div className="px-4 py-4 border-t border-white/5 bg-white/[0.02]">
      <p className="text-white/40 text-xs mb-2 font-semibold uppercase tracking-wider">Sider for "{role.name}"</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 mb-4">
        {pages.map(p => (
          <button key={p.path} onClick={() => toggle(p.path)}
            className={`text-left px-3 py-1.5 rounded text-xs transition-colors ${
              selected.includes(p.path) ? 'bg-[#1E6F7A]/20 text-[#1E6F7A] border border-[#1E6F7A]/30' : 'bg-white/5 text-white/40 border border-white/5'
            }`}>{p.label}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(role.name, selected)} className="px-4 py-1.5 bg-[#1E6F7A] text-white rounded text-sm font-medium">Gem</button>
        <button onClick={onCancel} className="px-4 py-1.5 bg-white/5 text-white/60 rounded text-sm">Annuller</button>
      </div>
    </div>
  );
}
